<template>
  <div
    class="card"
    style="border-top-left-radius: 0; border-top-right-radius: 0"
  >
    <div class="card-header">
      <div class="row mx-0 align-items-center w-100">
        <h4 class="col px-0 flex align-items-center w-100 mb-0">
          Brand Leaderboard

          <b-spinner
            v-if="isLoadingTotalSalesReport || isLoadingDailySalesReport"
            small
            label="Spinning"
            class="ml-50 mb-25"
          />
        </h4>

        <div class="col-auto px-0">
          <button
            class="btn btn-primary d-flex align-items-center"
            :disabled="!dailyReport.length || isLoadingDailySalesReport"
            @click="exportDailyReportToCsv"
          >
            <feather-icon
              icon="DownloadIcon"
              size="18"
              class="mr-1"
            />

            Export CSV
          </button>
        </div>
      </div>
    </div>
    <div class="card-body">
      <!-- Filters -->
      <div class="row mb-2">
        <div class="col-md-4">
          <b-form-group label="Brand">
            <v-select
              :value="selectedMerchant"
              :options="merchantList"
              label="name"
              :filterable="false"
              :disabled="isLoadingTotalSalesReport || isLoadingDailySalesReport"
              @search="onSearchMerchant"
              @input="value => $store.commit('brandLeaderboard/SET_SELECTED_MERCHANT', value)"
              @input.self="handleFilterChange"
            />
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group label="Dari Tanggal">
            <b-form-datepicker
              :value="startDate"
              :disabled="isLoadingTotalSalesReport || isLoadingDailySalesReport"
              @input="value => $store.commit('brandLeaderboard/SET_START_DATE', value)"
              @input.self="handleFilterChange"
            />
          </b-form-group>
        </div>
        <div class="col-md-4">
          <b-form-group label="Hingga Tanggal">
            <b-form-datepicker
              :value="endDate"
              :disabled="isLoadingTotalSalesReport || isLoadingDailySalesReport"
              @input="value => $store.commit('brandLeaderboard/SET_END_DATE', value)"
              @input.self="handleFilterChange"
            />
          </b-form-group>
        </div>
      </div>

      <!-- Summary Cards -->
      <div
        v-if="totalReport"
        class="row mb-2"
      >
        <div class="col-md-6">
          <div class="card bg-light mb-0">
            <div class="card-body">
              <h5 class="card-title d-flex align-items-center">
                Jumlah Transaksi

                <b-spinner
                  v-if="isLoadingTotalSalesReport"
                  small
                  label="Spinning"
                  class="ml-1"
                />
              </h5>
              <h3>{{ totalReport.transaction_count >= 0 ? `${totalReport.transaction_count} transaksi` : 'Memuat...' }}</h3>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card bg-light mb-0">
            <div class="card-body">
              <h5 class="card-title d-flex align-items-center">
                Total Penjualan

                <b-spinner
                  v-if="isLoadingTotalSalesReport"
                  small
                  label="Spinning"
                  class="ml-1"
                />
              </h5>
              <h3>{{ totalReport.total_sales >= 0 ? formatCurrency(totalReport.total_sales) : 'Memuat...' }}</h3>
            </div>
          </div>
        </div>
      </div>

      <!-- Daily Breakdown Table -->
      <div
        v-if="dailyReport && dailyReport.length"
        class="table-responsive"
      >
        <table
          class="table table-bordered"
          :class="{'opacity-50': isLoadingDailySalesReport}"
        >
          <thead>
            <tr>
              <th>Tanggal</th>
              <th>Jumlah Transaksi</th>
              <th>Total Penjualan</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in dailyReport"
              :key="item.date"
            >
              <td>{{ formatDate(item.date) }}</td>
              <td>{{ item.transaction_count }}</td>
              <td>{{ formatCurrency(item.total_sales) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="!dailyReport.length"
        class="text-center py-5"
      >
        Tidak ada data untuk ditampilkan.
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import {
  BFormGroup,
  BFormDatepicker, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  components: {
    BSpinner,
    BFormDatepicker,
    BFormGroup,
    vSelect,
  },
  computed: {
    ...mapState('brand', ['brandList']),
    ...mapState('brandLeaderboard', [
      'selectedMerchant',
      'merchantList',
      'limit',
      'startDate',
      'endDate',
      'totalReport',
      'dailyReport',
      'isLoadingTotalSalesReport',
      'isLoadingDailySalesReport',
    ]),
    ...mapGetters('brandLeaderboard', ['isLoading']),
  },
  async mounted() {
    if (!this.selectedMerchant) {
      await this.loadMerchant({ page: 1, keyword: 'miniapoly' })

      if (this.merchantList.length) {
        this.$store.commit('brandLeaderboard/SET_SELECTED_MERCHANT', this.merchantList[0])
      }
    }

    if (!this.totalReport || !this.totalReport.total_sales || !this.dailyReport || !this.dailyReport.length) {
      await this.fetchReports()
    }
  },
  methods: {
    ...mapActions('brand', ['getBrandList']),
    ...mapActions('brandLeaderboard', ['fetchReports']),
    async onSearchMerchant(text, loading) {
      if (text.length) {
        loading(true)
        this.searchMerchant(loading, text, this)
      }
    },
    searchMerchant: _.debounce((loading, search, that) => {
      that.loadMerchant({ keyword: search, page: 1 }).finally(() => {
        loading(false)
      })
    }, 500),
    async loadMerchant({ page = 1, keyword = undefined }) {
      const res = await this.getBrandList({
        limit: this.limit,
        page,
        keyword,
        hasSearch: true,
      })
      this.$store.commit('brandLeaderboard/SET_MERCHANT_LIST', res.data)
    },
    // reset other tab data to force reload data from API
    handleFilterChange() {
      // tab 1
      this.$store.commit('brandLeaderboard/RESET_MERCHANT_LEADERBOARD')
      // tab 3
      this.$store.commit('brandLeaderboard/RESET_TRANSACTION_ITEMS')
      this.fetchReports()
    },
    formatDate(date) {
      return moment(date).format('DD MMM YYYY')
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(amount || 0)
    },
    exportDailyReportToCsv() {
      if (!this.dailyReport.length) return

      // Define headers
      const headers = ['date', 'merchant_id', 'transaction_count', 'total_sales']

      // Convert data to CSV format
      const csvContent = [
        // Add headers
        headers.join(','),
        // Add data rows, wrap values in quotes to handle commas in data
        ...this.dailyReport.map(row => headers.map(header => `"${row[header]}"`).join(',')),
      ].join('\n')

      // Create blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `${this.selectedMerchant.name} Daily Sales Report ${this.startDate}-${this.endDate}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
.card {
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}
</style>
